import React, { useState, useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";
import "./DeleteApprovalTeamModal.css";

const MODE_REASSIGN = "reassign";
const MODE_NONE = "none";

export default function DeleteApprovalTeamModal({
  open,
  team,
  allTeams,
  onClose,
  onDeleted,
  deleteApprovalTeam,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(MODE_NONE);
  const [reassignTo, setReassignTo] = useState("");

  // Only offer reassignment to teams in the same company as the one being
  // deleted — allTeams can span every company the user manages.
  const otherTeams = (allTeams || []).filter(
    (t) => t.uuid !== team?.uuid && t.company_uuid === team?.company_uuid
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (otherTeams.length > 0) {
      setMode(MODE_REASSIGN);
      setReassignTo(otherTeams[0].uuid);
    } else {
      setMode(MODE_NONE);
      setReassignTo("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, team]);

  if (!open) return null;

  async function handleDelete() {
    setIsSaving(true);
    setError(null);

    const deleteOptions =
      mode === MODE_REASSIGN && reassignTo
        ? { new_approval_team_uuid: reassignTo }
        : { new_approval_team_uuid: null };

    const result = await deleteApprovalTeam(
      team.uuid,
      deleteOptions,
      team.company_uuid
    );

    setIsSaving(false);

    if (!result.success) {
      setError(
        result.error ||
          `Failed to delete approval team "${team?.name}". Please try again or contact support if the issue persists.`
      );
      return;
    }

    onDeleted();
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div
        className="jfm-container dtm-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dtm-header">
          <div className="dtm-header-icon">
            <AlertTriangle size={20} />
          </div>
          <div>
            <h3 className="dtm-header-title">Delete Approval Team</h3>
            <p className="dtm-header-subtitle">This action cannot be undone</p>
          </div>
          <button
            className="jfm-close"
            onClick={onClose}
            style={{ marginLeft: "auto" }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="dtm-body">
          <p className="dtm-prompt">
            Delete team <strong>"{team?.name}"</strong> and reassign existing
            job approvals to:
          </p>

          <label
            className={`dtm-option${
              mode === MODE_REASSIGN ? " dtm-option--selected" : ""
            }${otherTeams.length === 0 ? " dtm-option--disabled" : ""}`}
          >
            <div className="dtm-option-head">
              <input
                type="radio"
                name="approval-team-delete-mode"
                checked={mode === MODE_REASSIGN}
                disabled={isSaving || otherTeams.length === 0}
                onChange={() => setMode(MODE_REASSIGN)}
              />
              <span className="dtm-option-title">Reassign to another team</span>
            </div>
            {mode === MODE_REASSIGN && otherTeams.length > 0 && (
              <select
                className="dtm-option-select"
                value={reassignTo}
                disabled={isSaving}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setReassignTo(e.target.value)}
              >
                {otherTeams.map((t) => (
                  <option key={t.uuid} value={t.uuid}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
            {otherTeams.length === 0 && (
              <p className="dtm-option-subtitle">
                No other approval teams exist for this company.
              </p>
            )}
          </label>

          <label
            className={`dtm-option${
              mode === MODE_NONE ? " dtm-option--selected" : ""
            }`}
          >
            <div className="dtm-option-head">
              <input
                type="radio"
                name="approval-team-delete-mode"
                checked={mode === MODE_NONE}
                disabled={isSaving}
                onChange={() => setMode(MODE_NONE)}
              />
              <span className="dtm-option-title">No approval team</span>
            </div>
            <p className="dtm-option-subtitle">
              Remove the approval team from all pending entries and linked jobs
            </p>
          </label>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          <div />
          <div className="jfm-footer-actions">
            <button
              className="jfm-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="dtm-delete-btn"
              onClick={handleDelete}
              disabled={isSaving}
            >
              {isSaving ? "Deleting..." : "Delete Team"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
